/**
 * Explore filter state <-> URL query string. Keys are the API param names so
 * a URL can be sent straight to GET /api/items/.
 */

export const MULTI_KEYS = ['diagnosis', 'exit_lane', 'priority', 'universe', 'category', 'brand', 'color', 'material'];
export const NUMBER_KEYS = ['min_qty', 'max_unit_cost', 'min_unit_cost', 'max_price', 'min_completeness', 'max_completeness', 'min_age'];
export const TOGGLE_KEYS = ['supplier_return', 'bulky', 'outdoor', 'has_image', 'on_shortlist'];
/** Every key that counts as a filter (not q / sort / ui state). */
export const FILTER_KEYS = [...MULTI_KEYS, ...NUMBER_KEYS, ...TOGGLE_KEYS];

export const FACET_LABELS = {
  diagnosis: "Why it's stuck",
  exit_lane: 'Way out',
  priority: 'Priority',
  universe: 'Stock group',
  category: 'Category',
  brand: 'Brand',
  color: 'Colour',
  material: 'Material',
};

export const NUMBER_LABELS = {
  min_qty: 'At least (pieces)',
  max_unit_cost: 'Max purchase price',
  min_unit_cost: 'Min purchase price',
  max_price: 'Max shop price',
  min_completeness: 'Product info at least',
  max_completeness: 'Product info at most',
  min_age: 'In stock for at least (days)',
};

export const TOGGLE_LABELS = {
  supplier_return: 'Recently bought: ask supplier',
  bulky: 'Bulky',
  outdoor: 'Outdoor',
  has_image: 'Has image',
  on_shortlist: 'On a shortlist',
};

export const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'value', label: 'Value (high → low)' },
  { value: 'qty', label: 'Quantity (high → low)' },
  { value: 'sold12', label: 'Most sold (12 months)' },
  { value: 'sold6', label: 'Most sold (6 months)' },
  { value: 'age', label: 'Age (oldest first)' },
  { value: 'cost', label: 'Unit cost (high → low)' },
  { value: '-completeness', label: 'Completeness (high → low)' },
  { value: 'completeness', label: 'Completeness (low → high)' },
];

/** Quick views shown as chips above the results. */
export const QUICK_VIEWS = [
  { id: 'b2b4', label: 'B2B lots ≥ 4 pcs', hint: 'B2B project lot lane, at least 4 free units', params: { exit_lane: ['B2B project lot'], min_qty: '4' } },
  { id: 'signature', label: 'Signature pieces', hint: 'Design-led pieces for curated edits', params: { exit_lane: ['Signature'] } },
  { id: 'return', label: 'Ask supplier', hint: 'Bought in the last 12 months from a supplier we still order from: worth asking about a return or exchange (not confirmed)', params: { supplier_return: '1' } },
  { id: 'invisible', label: 'Hardly anyone sees it', hint: 'Few ad impressions, or listing < 50% complete', params: { diagnosis: ['Hardly anyone sees it'] } },
  { id: 'p1', label: 'Act now', hint: 'A lot of money stuck: start here', params: { priority: ['Act now'] } },
  { id: 'relist', label: 'Put back online', hint: "Good products customers can't find", params: { exit_lane: ['Put back online'] } },
  { id: 'offcat', label: 'Not on our website', hint: 'In stock but not listed on the webshop', params: { universe: ['Not on our website'] } },
];

/** Parse URLSearchParams into a filter object (arrays for multi keys). */
export function parseFilters(sp) {
  const f = {};
  MULTI_KEYS.forEach((k) => {
    const v = sp.getAll(k).filter(Boolean);
    if (v.length) f[k] = v;
  });
  [...NUMBER_KEYS, ...TOGGLE_KEYS].forEach((k) => {
    const v = sp.get(k);
    if (v !== null && v !== '') f[k] = v;
  });
  return f;
}

export function countFilters(f) {
  return FILTER_KEYS.reduce((n, k) => n + (Array.isArray(f[k]) ? f[k].length : f[k] !== undefined ? 1 : 0), 0);
}

/** Normalise an AI interpretation's filters into our shape (arrays for multi keys, strings otherwise). */
export function normaliseFilters(raw = {}) {
  const f = {};
  Object.entries(raw || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return;
    if (!FILTER_KEYS.includes(k)) return;
    if (MULTI_KEYS.includes(k)) f[k] = (Array.isArray(v) ? v : [v]).map(String);
    else if (TOGGLE_KEYS.includes(k)) f[k] = v === true ? '1' : v === false ? '0' : String(v);
    else f[k] = String(Array.isArray(v) ? v[0] : v);
  });
  return f;
}

/** Flatten filters into removable chips. */
export function filterChips(f) {
  const chips = [];
  MULTI_KEYS.forEach((k) => (f[k] || []).forEach((v) => chips.push({ key: k, value: v, label: `${FACET_LABELS[k]}: ${v}` })));
  NUMBER_KEYS.forEach((k) => {
    if (f[k] !== undefined) chips.push({ key: k, value: f[k], label: `${NUMBER_LABELS[k]}: ${f[k]}${k.includes('completeness') ? '%' : ''}` });
  });
  TOGGLE_KEYS.forEach((k) => {
    if (f[k] !== undefined) chips.push({ key: k, value: f[k], label: f[k] === '0' ? `Not ${TOGGLE_LABELS[k].toLowerCase()}` : TOGGLE_LABELS[k] });
  });
  return chips;
}

export function removeChip(f, chip) {
  const next = { ...f };
  if (MULTI_KEYS.includes(chip.key)) {
    next[chip.key] = (next[chip.key] || []).filter((v) => v !== chip.value);
    if (!next[chip.key].length) delete next[chip.key];
  } else delete next[chip.key];
  return next;
}

export function quickViewActive(f, view) {
  return Object.entries(view.params).every(([k, v]) => (Array.isArray(v) ? v.every((x) => (f[k] || []).includes(x)) : f[k] === v));
}

export function toggleQuickView(f, view) {
  const next = { ...f };
  const active = quickViewActive(f, view);
  Object.entries(view.params).forEach(([k, v]) => {
    if (Array.isArray(v)) {
      const cur = new Set(next[k] || []);
      v.forEach((x) => (active ? cur.delete(x) : cur.add(x)));
      if (cur.size) next[k] = [...cur];
      else delete next[k];
    } else if (active) delete next[k];
    else next[k] = v;
  });
  return next;
}
